// CameraController - handles camera movement and look-at targeting
import * as THREE from 'three';
import { CAMERA } from '../config/constants.js';

export class CameraController {
    constructor(camera, scene, config = {}) {
        this.camera = camera;
        this.scene = scene;

        // Dependencies (set via setDependencies)
        this.biomeManager = null;
        this.worldTranslator = null;
        this.lightingManager = null;

        // Camera settings from config or constants
        this.height = config.height || CAMERA.HEIGHT;
        this.lookAtDistance = config.lookAtDistance || CAMERA.LOOK_AT_DISTANCE;
        this.yawSmoothing = config.yawSmoothing || CAMERA.YAW_SMOOTHING;
        this.pitchSmoothing = config.pitchSmoothing || CAMERA.PITCH_SMOOTHING;
        this.yawOffset = config.yawOffset || CAMERA.YAW_OFFSET;
        this.maxPitch = config.maxPitch || CAMERA.MAX_PITCH;
        this.lateralOffset = config.lateralOffset || CAMERA.LATERAL_OFFSET;

        // Distance tracking
        this.travelDistance = 0;
        this.distance = 0;

        // Debug visualization
        this.debugLookAtEnabled = false;
        this.debugLookAtSphere = null;
    }

    /**
     * Set dependencies after construction
     */
    setDependencies(deps) {
        this.biomeManager = deps.biomeManager || this.biomeManager;
        this.worldTranslator = deps.worldTranslator || this.worldTranslator;
        this.lightingManager = deps.lightingManager || this.lightingManager;
    }

    /**
     * Initialize camera rotation to match starting road direction
     */
    initializeFromRoad() {
        if (!this.biomeManager) return;

        const initialRoadInfo = this.biomeManager.getRoadPositionAtZ(0);
        if (initialRoadInfo) {
            const { tangent } = initialRoadInfo;
            this.camera.rotation.y = Math.atan2(-tangent.x, -tangent.z);
            const horizontalLength = Math.sqrt(tangent.x * tangent.x + tangent.z * tangent.z);
            this.camera.rotation.x = Math.atan2(tangent.y, horizontalLength);
            this.camera.rotation.z = 0;
        }
    }

    /**
     * Main update method
     * @param {number} deltaTime - Time since last frame
     * @param {number} speed - Current movement speed
     */
    update(deltaTime, speed) {
        // Update world position
        if (this.worldTranslator) {
            this.worldTranslator.update(speed, deltaTime);
        }

        // Track travel distance
        const moveAmount = speed * deltaTime;
        this.travelDistance += Math.abs(moveAmount);
        this.distance = this.travelDistance;

        // Get world position
        const groupOffset = this.worldTranslator ? this.worldTranslator.getGroupOffset() : 0;
        const worldZ = -groupOffset;

        // Get road info at camera position and look-ahead point
        const roadInfo = this.biomeManager ? this.biomeManager.getRoadPositionAtZ(worldZ) : null;
        const lookAtWorldZ = worldZ - this.lookAtDistance;
        const lookAtInfo = this.biomeManager ? this.biomeManager.getRoadPositionAtZ(lookAtWorldZ) : null;

        if (roadInfo && lookAtInfo) {
            const { position } = roadInfo;
            const lookAtPosition = lookAtInfo.position;

            // Position camera on road surface
            this.camera.position.x = position.x + this.lateralOffset;
            this.camera.position.y = position.y + this.height;

            // Calculate direction to look-at point
            const dx = lookAtPosition.x - this.camera.position.x;
            const dy = lookAtPosition.y - this.camera.position.y;
            const dz = lookAtWorldZ - worldZ;

            // Calculate target yaw and pitch
            const targetYaw = Math.atan2(-dx, -dz) + this.yawOffset;
            const horizontalDist = Math.sqrt(dx * dx + dz * dz);
            let targetPitch = Math.atan2(dy, horizontalDist);
            targetPitch = Math.max(-this.maxPitch, Math.min(this.maxPitch, targetPitch));

            // Apply smoothing
            const yawSmooth = 1 - Math.exp(-this.yawSmoothing * deltaTime);
            const pitchSmooth = 1 - Math.exp(-this.pitchSmoothing * deltaTime);

            // Apply yaw with angle wrapping
            let yawDiff = targetYaw - this.camera.rotation.y;
            while (yawDiff > Math.PI) yawDiff -= Math.PI * 2;
            while (yawDiff < -Math.PI) yawDiff += Math.PI * 2;
            this.camera.rotation.y += yawDiff * yawSmooth;

            // Apply pitch
            this.camera.rotation.x += (targetPitch - this.camera.rotation.x) * pitchSmooth;

            // Roll is zero
            this.camera.rotation.z = 0;

            // Update debug sphere
            this._updateDebugSphere(lookAtPosition, lookAtWorldZ, groupOffset);
        }

        // Update headlights
        if (this.lightingManager) {
            this.lightingManager.updateHeadlightVisibility();
        }
    }

    _updateDebugSphere(lookAtPosition, lookAtWorldZ, groupOffset) {
        if (this.debugLookAtEnabled) {
            if (!this.debugLookAtSphere) {
                const sphereGeom = new THREE.SphereGeometry(2, 8, 8);
                const sphereMat = new THREE.MeshBasicMaterial({ color: 0xff0000 });
                this.debugLookAtSphere = new THREE.Mesh(sphereGeom, sphereMat);
                this.scene.add(this.debugLookAtSphere);
            }

            const visualZ = lookAtWorldZ + groupOffset;
            this.debugLookAtSphere.position.set(
                lookAtPosition.x,
                lookAtPosition.y + this.height,
                visualZ
            );
            this.debugLookAtSphere.visible = true;
        } else if (this.debugLookAtSphere) {
            this.debugLookAtSphere.visible = false;
        }
    }

    /**
     * Enable/disable debug look-at visualization
     */
    setDebugLookAtEnabled(enabled) {
        this.debugLookAtEnabled = enabled;
    }

    /**
     * Get travel distance
     */
    getTravelDistance() {
        return this.travelDistance;
    }

    /**
     * Set lateral offset
     */
    setLateralOffset(offset) {
        this.lateralOffset = offset;
    }
}
